import * as React from 'react';
import { TApiTaskEntity, TaskPriorityType } from '../../types/Tasks';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Box from '@mui/material/Box';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RepeatIcon from '@mui/icons-material/Repeat';
import EventIcon from '@mui/icons-material/Event';
import BuildIcon from '@mui/icons-material/Build';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { routes } from '../../Routes';
import { simpleDateTimeFormat } from '../../Helpers';

interface ITaskListItemProps {
  task: TApiTaskEntity;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '8px',
      marginBottom: theme.spacing(1.5),
      alignItems: 'flex-start',
      padding: theme.spacing(1.5),
    },
    title: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    description: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      maxWidth: '100%',
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      marginTop: theme.spacing(0.5),
    },
    dueDateOverdue: {
      color: theme.palette.error.main,
      fontSize: '0.8rem',
      fontWeight: 500,
    },
    dueDateFuture: {
      color: theme.palette.success.main,
      fontSize: '0.8rem',
      fontWeight: 500,
    },
    recurringIcon: {
      fontSize: '1rem',
      verticalAlign: 'middle',
      marginLeft: theme.spacing(0.5),
      color: theme.palette.text.secondary,
    },
    dueDateIcon: {
      fontSize: '1rem',
      verticalAlign: 'middle',
      marginRight: theme.spacing(0.5),
    },
    priorityIcon: {
      minWidth: '48px',
      marginTop: theme.spacing(0.5),
    },
    priorityCircle: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    serviceTypeBadge: {
      display: 'inline-block',
      backgroundColor: '#e3f2fd',
      color: '#1565c0',
      borderRadius: '4px',
      padding: '1px 6px',
      fontSize: '0.75rem',
      fontWeight: 500,
      marginLeft: theme.spacing(1),
    },
  })
);

const getPriorityIcon = (priorityType: TaskPriorityType | null) => {
  switch (priorityType) {
    case TaskPriorityType.Low:
      return {
        icon: <ArrowDownwardIcon style={{ color: '#fff' }} />,
        bgColor: '#999',
      };
    case TaskPriorityType.Medium:
      return {
        icon: <ArrowUpwardIcon style={{ color: '#fff' }} />,
        bgColor: '#2196f3',
      };
    case TaskPriorityType.Important:
      return {
        icon: <ArrowUpwardIcon style={{ color: '#fff' }} />,
        bgColor: '#ff9800',
      };
    case TaskPriorityType.Urgent:
      return {
        icon: <ArrowUpwardIcon style={{ color: '#fff' }} />,
        bgColor: '#f44336',
      };
    default:
      return null;
  }
};

const isDueDatePast = (dueDate: string | null): boolean => {
  if (!dueDate) return false;
  const now = new Date();
  return new Date(dueDate) < now;
};

const getDaysUntilDue = (dueDate: string): string => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffMs = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return '(today)';
  if (diffDays === 1) return '(1 day)';
  if (diffDays > 1) return `(${diffDays} days)`;
  return '';
};

const stripHtml = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

const TaskListItem: React.FC<ITaskListItemProps> = ({ task }): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();
  const history = useHistory();
  const overdue = isDueDatePast(task.dueDate);
  const priority = getPriorityIcon(task.priorityType);
  const isServiceTask = task.serviceTaskType != null;
  const secondaryText =
    isServiceTask && task.parentEquipmentName
      ? task.parentEquipmentName
      : stripHtml(task.description);

  const handleClick = () => {
    history.push(routes.taskDetail.replace(':id', String(task.id)));
  };

  const secondaryContent = (
    <React.Fragment>
      <Typography
        component="span"
        variant="body2"
        className={classes.description}
      >
        {secondaryText}
      </Typography>
      <Box display="flex" alignItems="center" mt={0.5}>
        {task.dueDate && (
          <Typography
            component="span"
            className={
              overdue ? classes.dueDateOverdue : classes.dueDateFuture
            }
          >
            <EventIcon
              className={classes.dueDateIcon}
              style={{
                color: overdue ? '#f44336' : '#4caf50',
              }}
            />
            {t('tasks.due_date')}: {simpleDateTimeFormat(new Date(task.dueDate))}
            {!overdue && ' ' + getDaysUntilDue(task.dueDate)}
          </Typography>
        )}
        {task.isRecurring && <RepeatIcon className={classes.recurringIcon} />}
      </Box>
    </React.Fragment>
  );

  return (
    <ListItem className={classes.root} button onClick={handleClick}>
      <ListItemIcon className={classes.priorityIcon}>
        {isServiceTask ? (
          <div
            className={classes.priorityCircle}
            style={{ backgroundColor: '#1565c0' }}
          >
            <BuildIcon style={{ color: '#fff' }} />
          </div>
        ) : (
          priority && (
            <div
              className={classes.priorityCircle}
              style={{ backgroundColor: priority.bgColor }}
            >
              {priority.icon}
            </div>
          )
        )}
      </ListItemIcon>
      <ListItemText
        primary={
          <>
            {task.title}
            {isServiceTask && task.serviceTaskTypeName && (
              <span className={classes.serviceTypeBadge}>
                {task.serviceTaskTypeName}
              </span>
            )}
          </>
        }
        secondary={secondaryContent}
        primaryTypographyProps={{
          variant: 'subtitle1',
          className: classes.title,
        }}
      />
    </ListItem>
  );
};

export default TaskListItem;
